import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "@/api/endpoints";
import { useAuthStore } from "@/store/authStore";
import { useToast } from "@/components/shared/Toast";

interface Props {
  onClose: () => void;
}

export function ChangePasswordDialog({ onClose }: Props) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pending, setPending] = useState(false);
  const showToast = useToast((s) => s.show);
  const clearAuth = useAuthStore((s) => s.clear);
  const nav = useNavigate();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== confirm) {
      showToast("New passwords do not match.", "error");
      return;
    }
    if (next.length < 12) {
      showToast("New password must be at least 12 characters.", "error");
      return;
    }
    if (next === current) {
      showToast("New password must differ from current.", "error");
      return;
    }
    setPending(true);
    try {
      await auth.changeMyPassword(current, next);
      showToast("Password changed. Please sign in again.", "success");
      // All refresh sessions were just revoked server-side. Force re-login.
      clearAuth();
      onClose();
      nav("/login", { replace: true });
    } catch (err) {
      showToast(err instanceof Error ? err.message : "password change failed", "error");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-3 rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900"
      >
        <h2 className="text-base font-semibold">Change password</h2>
        <p className="text-xs text-slate-500">
          Changing your password signs out every other session, including this one.
          You'll be redirected to sign in again.
        </p>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Current password</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:bg-slate-800 dark:border-slate-700"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">New password</span>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:bg-slate-800 dark:border-slate-700"
          />
          <span className="text-xs text-slate-500">Min 12 characters.</span>
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Confirm new password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:bg-slate-800 dark:border-slate-700"
          />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending}
            className="px-3 py-2 text-sm rounded-md bg-brand-500 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {pending ? "Saving…" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}
